/**
 * The platform screen's calls — `/v1/platform`, operators only. Its own
 * client like every other feature's, so it can be deleted in one directory.
 */

import type { FeatureId, FeaturePresetId, FeatureSwitches } from "@baros/core";
import { request as shared } from "../api.js";

async function request<T>(token: string, method: "GET" | "POST" | "PUT" | "DELETE", path: string, body?: unknown): Promise<T> {
  // The shared client: it refreshes an expired token once and ends
  // the session when it cannot (see `request` in src/api.ts).
  return shared<T>(token, method, path, body);
}

export interface OrgHistoryEntry {
  readonly period: string;
  /** What the org is charged: chat, searches and shelf storage, at its multiplier. */
  readonly costCents: number;
  /** What AWS charged Venox for the same. */
  readonly baseCents: number;
  /** The shelf-storage part of `costCents`. */
  readonly storageCents: number;
  readonly turns: number;
  readonly tokens: number;
}

export interface PlatformOrg {
  readonly orgId: string;
  readonly name: string;
  readonly status: "active" | "suspended" | string;
  readonly createdAt: string | null;
  readonly suspendedAt: string | null;
  readonly suspendReason: string | null;
  readonly venues: number;
  readonly members: number;
  readonly aiPlanCeilingCents: number;
  readonly aiMonthlyLimitCents: number;
  /** What the org is charged per cent of what AWS charges Venox. */
  readonly aiPriceMultiplier: number;
  /** The recipe shelf as it stands: its size and a month of it, both prices. */
  readonly storage: { readonly bytes: number; readonly baseCents: number; readonly chargedCents: number };
  readonly usage: {
    readonly period: string;
    readonly costCents: number;
    readonly baseCents: number;
    readonly turns: number;
  };
  /** The last six periods, newest first — `history[0]` is always `usage`. */
  readonly history: readonly OrgHistoryEntry[];
  /** Feature switches (D14): stored, resolved, and the preset they match. */
  readonly features?: OrgFeatures;
}

export interface NewOrg {
  readonly name: string;
  readonly ownerEmail: string;
  readonly ownerName: string;
  readonly aiPlanCeilingCents: number;
  readonly venue: { readonly name: string; readonly suburb: string; readonly state: string } | null;
  /** Which features it starts with (D14). */
  readonly preset: FeaturePresetId;
}

export interface OrgFeatures {
  readonly switches: FeatureSwitches;
  readonly enabled: readonly FeatureId[];
  readonly preset: FeaturePresetId | null;
}

export interface OrgPatch {
  readonly name?: string;
  readonly status?: "active" | "suspended";
  readonly reason?: string;
  readonly aiPlanCeilingCents?: number;
  readonly aiPriceMultiplier?: number;
}

export const platformApi = {
  list: (token: string) =>
    request<{ orgs: PlatformOrg[]; acting: string | null }>(token, "GET", "/v1/platform/orgs"),
  create: (token: string, body: NewOrg) =>
    request<{ org: PlatformOrg; owner: { userId: string; email: string; invited: boolean } }>(
      token,
      "POST",
      "/v1/platform/orgs",
      body,
    ),
  patch: (token: string, orgId: string, body: OrgPatch) =>
    request<{ org: PlatformOrg }>(token, "PUT", `/v1/platform/orgs/${encodeURIComponent(orgId)}`, body),
  /** A preset, individual switches merged over what is stored, or both (D14). */
  setFeatures: (token: string, orgId: string, body: { preset?: FeaturePresetId; features?: FeatureSwitches }) =>
    request<{ features: OrgFeatures }>(token, "PUT", `/v1/platform/orgs/${encodeURIComponent(orgId)}/features`, body),
  /** Only a suspended organisation; removes every item it owns. */
  remove: (token: string, orgId: string) =>
    request<{ deleted: { orgId: string; items: number; venues: number } }>(
      token,
      "DELETE",
      `/v1/platform/orgs/${encodeURIComponent(orgId)}`,
    ),
};
